/**
 * Тайловая сетка для DEV-слоя "Границы тайлов"
 *
 * Генерирует GeoJSON FeatureCollection полигонов тайлов (z/x/y),
 * покрывающих текущий viewport карты.
 * Каждая фича содержит properties: { z, x, y, label: "z/x/y" }
 *
 * Алгоритм: lon2tile / lat2tile по стандартным формулам OSM/Mapbox.
 */

const DEG2RAD = Math.PI / 180;

function lon2tile (lon, zoom) {
    return Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
}

function lat2tile (lat, zoom) {
    return Math.floor(
        (1 - Math.log(Math.tan(lat * DEG2RAD) + 1 / Math.cos(lat * DEG2RAD)) / Math.PI)
        / 2 * Math.pow(2, zoom)
    );
}

function tile2lon (x, zoom) {
    return x / Math.pow(2, zoom) * 360 - 180;
}

function tile2lat (y, zoom) {
    const n = Math.PI - 2 * Math.PI * y / Math.pow(2, zoom);
    return 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/**
 * Генерирует GeoJSON тайловой сетки для текущего viewport карты.
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @returns {Object} GeoJSON FeatureCollection
 */
export function generateTileGridGeoJSON (map) {
    const zoom = Math.floor(map.getZoom());
    const bounds = map.getBounds();
    const minLon = bounds.getWest();
    const maxLon = bounds.getEast();
    const minLat = Math.max(bounds.getSouth(), -85.0511);
    const maxLat = Math.min(bounds.getNorth(),  85.0511);
    const xMin = lon2tile(minLon, zoom);
    const xMax = lon2tile(maxLon, zoom);
    const yMin = lat2tile(maxLat, zoom); // y-axis inverted: maxLat → smaller y
    const yMax = lat2tile(minLat, zoom);
    const maxTiles = Math.pow(2, zoom);
    const features = [];

    for (let x = xMin; x <= xMax; x++) {
        for (let y = yMin; y <= yMax; y++) {
            const safeX = ((x % maxTiles) + maxTiles) % maxTiles;
            const safeY = ((y % maxTiles) + maxTiles) % maxTiles;
            const west  = tile2lon(safeX,     zoom);
            const east  = tile2lon(safeX + 1, zoom);
            const north = tile2lat(safeY,     zoom);
            const south = tile2lat(safeY + 1, zoom);

            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [ [
                        [ west,  south ],
                        [ west,  north ],
                        [ east,  north ],
                        [ east,  south ],
                        [ west,  south ],
                    ] ],
                },
                properties: {
                    z: zoom,
                    x: safeX,
                    y: safeY,
                    label: `${zoom}/${safeX}/${safeY}`,
                },
            });
        }
    }

    return { type: 'FeatureCollection', features };
}

export const TILE_GRID_SOURCE_ID = 'dev-tile-boundaries-source';
export const TILE_GRID_FILL_LAYER_ID  = 'dev-tile-boundaries-fill';
export const TILE_GRID_LINE_LAYER_ID  = 'dev-tile-boundaries-line';
export const TILE_GRID_LABEL_LAYER_ID = 'dev-tile-boundaries-label';

/**
 * Инициализировать source и слои тайловой сетки на карте.
 * Вызывать один раз после загрузки карты.
 */
export function initTileGridLayers (map) {
    if (map.getSource(TILE_GRID_SOURCE_ID)) return;

    map.addSource(TILE_GRID_SOURCE_ID, {
        type: 'geojson',
        data: generateTileGridGeoJSON(map),
    });

    // Полупрозрачная заливка
    map.addLayer({
        id: TILE_GRID_FILL_LAYER_ID,
        type: 'fill',
        source: TILE_GRID_SOURCE_ID,
        layout: { visibility: 'none' },
        paint: {
            'fill-color': '#ff0000',
            'fill-opacity': 0.03,
        },
    });

    // Контуры тайлов
    map.addLayer({
        id: TILE_GRID_LINE_LAYER_ID,
        type: 'line',
        source: TILE_GRID_SOURCE_ID,
        layout: { visibility: 'none' },
        paint: {
            'line-color': '#ff0000',
            'line-width': 1,
            'line-opacity': 0.7,
        },
    });

    // Подписи z/x/y
    map.addLayer({
        id: TILE_GRID_LABEL_LAYER_ID,
        type: 'symbol',
        source: TILE_GRID_SOURCE_ID,
        layout: {
            visibility: 'none',
            'text-field': [ 'get', 'label' ],
            'text-size': 11,
            'text-anchor': 'center',
            'text-allow-overlap': false,
        },
        paint: {
            'text-color': '#ff0000',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
        },
    });
}

/**
 * Обновить данные тайловой сетки (вызывать на moveend/zoomend).
 */
export function updateTileGridData (map) {
    const source = map.getSource(TILE_GRID_SOURCE_ID);
    if (!source) return;
    source.setData(generateTileGridGeoJSON(map));
}

/**
 * Показать / скрыть слои тайловой сетки.
 */
export function setTileGridVisibility (map, visible) {
    const vis = visible ? 'visible' : 'none';
    [ TILE_GRID_FILL_LAYER_ID, TILE_GRID_LINE_LAYER_ID, TILE_GRID_LABEL_LAYER_ID ].forEach(id => {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
    });
    // При показе — сразу обновляем данные под текущий viewport
    if (visible) updateTileGridData(map);
}
